"use client";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Flex } from "@radix-ui/themes";
import { ComponentProps, useMemo } from "react";
import { makeTime } from "@/utils/general";

interface ITPicker extends ComponentProps<typeof Flex> {
    title: string;
    // An empty time slot has no hour/minute yet, so these are optional rather than cast at the call site.
    hours?: number;
    minutes?: number;
    onTimeChange: (time: Date | null) => void;
    tPickerWidth?: string | number;
    tPickerHeight?: string | number;
    hasError?: boolean;
}

// hours & minutes should be UTC values
export default function TPicker({ title, hours, minutes, onTimeChange, tPickerWidth, tPickerHeight, hasError, ...flexProps }: ITPicker) {
    // An empty time slot has no hour/minute yet, which must be represented as null rather than an
    // Invalid Date. makeTime() also seeds seconds/ms from the current clock, so the value is pinned
    // to :00 and memoised - otherwise every render produces a different timestamp and MUI resets the
    // input, making it impossible to type.
    const value = useMemo(() => {
        // typeof narrows the optional props for makeTime; Number.isFinite is not a type guard and would
        // still leave them possibly undefined.
        if (typeof hours !== "number" || typeof minutes !== "number" || !Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
        const time = makeTime(hours, minutes);
        time.setSeconds(0, 0);
        return time;
    }, [hours, minutes]);

    const handleChange = (newValue: Date | null) => {
        // Ignore the Invalid Date that MUI emits for partially typed input so the picker can keep
        // its own editing state; publish only cleared (null) or complete values.
        if (newValue && newValue.toString() === "Invalid Date") return;
        onTimeChange(newValue);
    };

    return (
        <Flex {...flexProps}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                {/* DesktopTimePicker, not the responsive TimePicker: the responsive one switches to the
                    mobile variant on coarse-pointer devices, where the field is read-only by design and
                    can only be set through the clock dialog. This screen needs the time to be typeable. */}
                <DesktopTimePicker
                    sx={{
                        width: tPickerWidth || "55%",
                        height: "55%",
                        // x-date-pickers v9 renamed the field classes (MuiInputBase-* -> MuiPickers*) and
                        // the visible text now lives in the sections container; the actual <input> is
                        // aria-hidden, so padding has to be applied to the container instead.
                        "& .MuiPickersInputBase-root": { borderRadius: "8px" },
                        "& .MuiPickersInputBase-sectionsContainer": {
                            padding: "12px 0 12px 12px",
                        },
                        "& .MuiPickersOutlinedInput-notchedOutline": {
                            borderColor: hasError ? "#d32f2f !important" : "",
                        },
                    }}
                    label={title}
                    views={["hours", "minutes"]}
                    ampm={true}
                    value={value}
                    onChange={handleChange}
                />
            </LocalizationProvider>
        </Flex>
    );
}
